import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import {
    LoadingComponent,
    ErrorDataComponent,
} from '../loadingErrorNoDataComponents';
import markdownComponents from './markdownComponents';

export const MarkdownViewer = ({ fileName, fileUrl, scrollToLine }) => {
    const [lines, setLines] = useState([]);
    const [visibleLinesCount, setVisibleLinesCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const lineRefs = useRef({});
    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);

    const CHUNK_SIZE = 500;

    useEffect(() => {
        const fetchMarkdownContent = async () => {
            try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error('Failed to fetch markdown content');
                }
                const text = await response.text();
                // Replace special characters
                const processedText = text
                    .replace(/\(c\)/gi, '&copy;')
                    .replace(/\(r\)/gi, '&reg;')
                    .replace(/\(tm\)/gi, '&trade;')
                    .replace(/\(p\)/gi, '&para;')
                    .replace(/\+-/g, '&plusmn;');

                const splitLines = processedText.split('\n');
                setLines(splitLines);
                setVisibleLinesCount(Math.min(CHUNK_SIZE, splitLines.length));
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (fileUrl) {
            fetchMarkdownContent();
        }
    }, [fileUrl]);

    useEffect(() => {
        const root = scrollContainerRef.current;
        const sentinel = sentinelRef.current;
        if (!root || !sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) {
                    return;
                }

                setVisibleLinesCount((current) => {
                    if (!lines.length) {
                        return current;
                    }
                    const next = Math.min(current + CHUNK_SIZE, lines.length);
                    return next;
                });
            },
            {
                root,
                rootMargin: '300px',
                threshold: 0,
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [lines.length]);

    useEffect(() => {
        if (!scrollToLine || !lines.length) {
            return;
        }

        if (scrollToLine > visibleLinesCount) {
            const required = Math.min(
                lines.length,
                Math.ceil(scrollToLine / CHUNK_SIZE) * CHUNK_SIZE
            );
            setVisibleLinesCount(required);
        }
    }, [scrollToLine, lines.length, visibleLinesCount]);

    // Scroll to the specified line after lines are loaded
    useEffect(() => {
        if (scrollToLine && lineRefs.current[scrollToLine]) {
            const timeout = setTimeout(() => {
                lineRefs.current[scrollToLine].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [scrollToLine, visibleLinesCount]);

    if (isLoading) return <LoadingComponent fileName={fileName} />;
    if (error) return <ErrorDataComponent fileName={fileName} />;

    if (!lines.length) return null;

    const visibleLines = lines.slice(0, visibleLinesCount);

    return (
        <div className='markdown-content relative h-full'>
            <div
                ref={scrollContainerRef}
                className='absolute inset-0 overflow-auto p-4'
            >
                {visibleLines.map((line, index) => {
                    const lineNumber = index + 1;
                    return (
                        <div
                            key={index}
                            ref={(el) => (lineRefs.current[lineNumber] = el)}
                            className={`mb-2`}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkEmoji]}
                                components={markdownComponents}
                            >
                                {line}
                            </ReactMarkdown>
                        </div>
                    );
                })}
                <div ref={sentinelRef} />
            </div>
        </div>
    );
};
